import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { API, api, getJwt, fileSize } from '../lib/api'
import { slugify } from '../lib/slug'
import Layout, { StatusBadge } from '../components/Layout'
import { useUploads } from '../lib/uploads'
import ReviewLink, { copyText, reviewStatsLabel, type ReviewShare } from '../components/ReviewLink'
import { Msg, inputCls, fmtDate } from '../components/ui'
import { REVIEW_STATUSES, clientLabel, type Client, type Project, type Video, reviewTarget } from '../lib/db'

interface F { name: string; size: number; modified: string }
interface ReviewLinkRow extends ReviewShare { recent: { id: string; file: string; author: string; text: string; time: number | null; resolved: boolean; created_at: string }[] }
type Kind = 'video' | 'image' | 'other'

const kindOf = (n: string): Kind => /\.(mp4|mov|webm|m4v|mkv|avi)$/i.test(n) ? 'video' : /\.(jpe?g|png|gif|webp|heic|avif|tiff?)$/i.test(n) ? 'image' : 'other'
const ICON: Record<Kind, string> = { video: '🎬', image: '🖼️', other: '📎' }
const SECTION: Record<Kind, string> = { video: 'Vidéos', image: 'Photos', other: 'Autres fichiers' }
const btn = 'px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors'
const fmtTc = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`

function Thumb({ src, kind }: { src: string; kind: Kind }) {
  const [err, setErr] = useState(false)
  if (kind === 'other' || err) return <div className="w-full h-full flex items-center justify-center text-4xl">{ICON[kind]}</div>
  return <img src={src} alt="" loading="lazy" onError={() => setErr(true)} className="w-full h-full object-cover" />
}

/** Visionneuse plein écran des livrables (photo / vidéo), flèches ← → */
function Preview({ items, index, url, onClose, onNav }: { items: F[]; index: number; url: (f: F) => string; onClose: () => void; onNav: (i: number) => void }) {
  const f = items[index]; const k = kindOf(f.name)
  useEffect(() => {
    const h = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose(); if (ev.key === 'ArrowLeft' && index > 0) onNav(index - 1); if (ev.key === 'ArrowRight' && index < items.length - 1) onNav(index + 1) }
    window.addEventListener('keydown', h); document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = '' }
  }, [index, items.length, onClose, onNav])
  const navBtn = 'absolute top-1/2 -translate-y-1/2 text-4xl md:text-5xl text-blanc/80 hover:text-orange bg-black/50 md:bg-transparent rounded-full w-11 h-11 flex items-center justify-center leading-none'
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={onClose}>
      <div className="flex items-center gap-4 px-4 py-3 text-sm" onClick={ev => ev.stopPropagation()}>
        <span className="flex-1 truncate">{ICON[k]} {f.name}</span>
        <span className="text-acier text-xs hidden sm:inline">{fileSize(f.size)} · {index + 1}/{items.length}</span>
        <a href={url(f) + '&download=1'} className="text-orange text-xs font-bold uppercase tracking-widest hover:underline">⬇ Télécharger</a>
        <button onClick={onClose} aria-label="Fermer" className="text-acier hover:text-orange text-3xl leading-none">×</button>
      </div>
      <div className="flex-1 min-h-0 relative flex items-center justify-center px-2 md:px-16 pb-3">
        {index > 0 && <button onClick={ev => { ev.stopPropagation(); onNav(index - 1) }} className={`${navBtn} left-2`}>‹</button>}
        <div className="w-full h-full flex items-center justify-center" onClick={ev => { if (ev.target === ev.currentTarget) onClose() }}>
          {k === 'image' && <img src={url(f)} alt={f.name} className="max-w-full max-h-full object-contain" />}
          {k === 'video' && <video key={f.name} src={url(f)} controls autoPlay playsInline className="max-w-full max-h-full" />}
        </div>
        {index < items.length - 1 && <button onClick={ev => { ev.stopPropagation(); onNav(index + 1) }} className={`${navBtn} right-2`}>›</button>}
      </div>
    </div>
  )
}

export default function Livrables() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const clientId = params.get('client') || ''
  const setClientId = (id: string) => setParams(id ? { client: id } : {})
  const [clients, setClients] = useState<Client[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [videos, setVideos] = useState<Video[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [links, setLinks] = useState<ReviewLinkRow[]>([])
  const [files, setFiles] = useState<F[]>([])
  const [jwt, setJwt] = useState('')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const [drag, setDrag] = useState(false)
  const [preview, setPreview] = useState<number | null>(null)
  const [filter, setFilter] = useState('tous')
  const videoInput = useRef<HTMLInputElement>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  const { start } = useUploads()

  const client = clients.find(c => c.id === clientId)
  const slug = client ? slugify(client.full_name || client.company) : ''

  async function load() {
    const [c, p, v, k] = await Promise.all([
      supabase.from('clients').select('*').order('company').order('full_name'),
      supabase.from('projects').select('*'),
      supabase.from('videos').select('*').order('created_at', { ascending: false }),
      supabase.from('video_comments').select('video_id,is_resolved'),
    ])
    setClients((c.data as any) || []); setProjects((p.data as any) || []); setVideos((v.data as any) || [])
    const m: Record<string, number> = {}; ((k.data as any) || []).forEach((x: any) => { if (!x.is_resolved) m[x.video_id] = (m[x.video_id] || 0) + 1 }); setCounts(m)
    loadLinks()
  }
  const loadLinks = () => api<{ reviews: ReviewLinkRow[] }>('/stock/reviews').then(j => setLinks(j.reviews)).catch(() => {})
  async function loadFiles(s: string) {
    if (!s) { setFiles([]); return }
    try { const j = await api<{ files: F[] }>(`/deliverables?slug=${s}`); setFiles(j.files || []) } catch { setFiles([]) }
  }
  useEffect(() => { getJwt().then(setJwt); load() }, [])
  useEffect(() => { loadFiles(slug); setPreview(null) }, [slug])

  const cname = (id: string | null) => clientLabel(clients.find(c => c.id === id))
  const pname = (id: string | null) => projects.find(p => p.id === id)?.title || '—'
  const clientOfLink = (l: ReviewShare) => l.root === 'clients' ? clients.find(c => slugify(c.full_name || c.company) === l.path.split('/')[0]) : undefined
  const url = (f: F) => `${API}/deliverable?slug=${slug}&name=${encodeURIComponent(f.name)}&access_token=${jwt}`
  const thumb = (f: F) => url(f) + '&thumb=1'
  const previews = files.filter(f => kindOf(f.name) !== 'other')

  async function doUpload(list: FileList | File[], label: string) {
    const arr = Array.from(list); if (!arr.length || !slug) return
    setBusy(true); setMsg(`Téléversement de ${arr.length} ${label}${arr.length > 1 ? 's' : ''}…`)
    try { await start(`/upload-deliverable?slug=${slug}`, arr); setMsg(`✅ ${arr.length} ${label}${arr.length > 1 ? 's' : ''} ajouté${arr.length > 1 ? 's' : ''}. ${links.some(l => l.root === 'clients' && l.path === slug) ? 'Visible immédiatement sur le lien de revue du client.' : ''}`); await loadFiles(slug) }
    catch (e: any) { setMsg('Erreur : ' + e.message) } finally { setBusy(false) }
  }
  async function removeFile(f: F) {
    if (!confirm(`Supprimer définitivement « ${f.name} » des livrables de ${clientLabel(client)} ?`)) return
    try { await api(`/deliverable?slug=${slug}&name=${encodeURIComponent(f.name)}`, { method: 'DELETE' }); setMsg('Fichier supprimé.'); await loadFiles(slug) } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }
  /** Revue via le portail (avec compte) : crée ou retrouve l'entrée `videos` puis ouvre /revue/:id */
  async function portalReview(name: string) {
    const file_path = `${slug}/${name}`
    const { data: ex } = await supabase.from('videos').select('id').eq('file_path', file_path).maybeSingle()
    let vid = (ex as any)?.id
    if (!vid) {
      const { data, error } = await supabase.from('videos').insert({ title: name.replace(/\.[^.]+$/, ''), file_name: name, file_path, client_id: clientId, project_id: projects.find(p => p.client_id === clientId)?.id || null }).select('id').single()
      if (error) { setMsg('Erreur : ' + error.message); return } vid = (data as any).id
    }
    nav(`/revue/${vid}`)
  }
  async function setStatus(v: Video, s: string) { await supabase.from('videos').update({ review_status: s }).eq('id', v.id); load() }
  async function removeVideo(v: Video) {
    if (!confirm(`Retirer « ${v.title} » de la revue portail ? Le fichier reste dans les livrables.`)) return
    await supabase.from('video_comments').delete().eq('video_id', v.id)
    const { error } = await supabase.from('videos').delete().eq('id', v.id)
    if (error) setMsg('Erreur : ' + error.message); else load()
  }
  async function revokeLink(l: ReviewShare) {
    if (!confirm(`Révoquer le lien de revue « ${l.path} » ? Les commentaires seront effacés.`)) return
    try { await api(`/stock/share?token=${l.token}`, { method: 'DELETE' }); loadLinks(); setMsg('Lien révoqué.') } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }

  const inPortal = (name: string) => videos.find(v => v.file_path === `${slug}/${name}`)
  const filteredVideos = videos.filter(v => (filter === 'tous' || v.review_status === filter) && (!clientId || v.client_id === clientId))
  const sections = (['video', 'image', 'other'] as Kind[]).map(k => ({ kind: k, items: files.filter(f => kindOf(f.name) === k) })).filter(s => s.items.length)

  return (
    <Layout title="Livrables & revue">
      {preview !== null && previews[preview] && <Preview items={previews} index={preview} url={url} onClose={() => setPreview(null)} onNav={setPreview} />}

      {/* Sélection du client */}
      <div className="flex flex-wrap items-center gap-3">
        <select className={inputCls + ' w-full max-w-md'} value={clientId} onChange={e => setClientId(e.target.value)}>
          <option value="">— Sélectionner un client —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
        </select>
        {client && <a href={`/admin/clients/${client.id}`} className="text-xs uppercase tracking-widest text-acier hover:text-orange">Fiche client →</a>}
      </div>
      <Msg msg={msg} />

      {client && (
        <>
          {/* Lien de revue public */}
          <section className="mt-6 border border-sky-300/30 rounded bg-sky-300/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-xl tracking-wide">💬 Lien de revue pour {clientLabel(client)}</h2>
                <p className="text-acier text-xs mt-1">Public, sans compte et sans téléchargement : le client voit toutes les vidéos et photos ci-dessous, commente à un moment précis d'une vidéo ou sur un point d'une photo, et approuve chaque fichier. Vous recevez un courriel à chaque activité.</p>
              </div>
              <ReviewLink slug={slug} disabled={previews.length === 0} onMsg={m => { setMsg(m); loadLinks() }} />
            </div>
          </section>

          {/* Livrables : téléversement + vignettes par type */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl tracking-wide">Livrables <span className="text-acier text-sm font-sans tracking-normal">· dossier <code className="text-orange">{slug}</code> · {files.length} fichier{files.length > 1 ? 's' : ''}</span></h2>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => videoInput.current?.click()} disabled={busy} className={`${btn} bg-orange hover:bg-orange-hover disabled:opacity-50`}>🎬 Ajouter des vidéos</button>
              <button onClick={() => photoInput.current?.click()} disabled={busy} className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50`}>🖼️ Ajouter des photos</button>
              <input ref={videoInput} type="file" multiple accept="video/*,.mov,.mp4,.m4v,.webm,.mkv" className="hidden" onChange={e => { if (e.target.files) doUpload(e.target.files, 'vidéo'); e.target.value = '' }} />
              <input ref={photoInput} type="file" multiple accept="image/*,.heic,.jpg,.jpeg,.png,.webp,.tif,.tiff" className="hidden" onChange={e => { if (e.target.files) doUpload(e.target.files, 'photo'); e.target.value = '' }} />
            </div>
          </div>

          <div onDragOver={e => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)} onDrop={e => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files, 'fichier') }}
            className={`mt-3 border rounded p-4 transition-colors ${drag ? 'border-orange bg-orange/5' : 'border-white/10'}`}>
            {files.length === 0 && <p className="text-acier text-sm text-center py-8">Aucun livrable. Glissez-déposez des vidéos ou des photos ici, ou utilisez les boutons ci-dessus.</p>}
            {sections.map(s => (
              <div key={s.kind} className="mb-6 last:mb-0">
                <h3 className="text-[11px] uppercase tracking-widest text-acier mb-3">{ICON[s.kind]} {SECTION[s.kind]} · {s.items.length}</h3>
                <ul className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {s.items.map(f => {
                    const k = kindOf(f.name); const pv = previews.indexOf(f); const pr = k === 'video' ? inPortal(f.name) : undefined
                    return (
                      <li key={f.name} className="group border border-white/10 rounded overflow-hidden bg-white/[0.02] flex flex-col">
                        <button onClick={() => pv >= 0 ? setPreview(pv) : window.open(url(f), '_blank')} className="relative block w-full aspect-[16/10] bg-[#161616] overflow-hidden" title="Aperçu">
                          {jwt && <Thumb src={thumb(f)} kind={k} />}
                          {k === 'video' && <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 border-2 border-white flex items-center justify-center text-sm pl-0.5">▶</span>}
                          {pr && <span className="absolute top-2 right-2"><StatusBadge status={pr.review_status || 'en_attente'} /></span>}
                        </button>
                        <div className="p-3 flex-1 flex flex-col gap-2">
                          <span className="text-sm truncate" title={f.name}>{f.name}</span>
                          <div className="text-[11px] text-acier">{fileSize(f.size)} · {fmtDate(f.modified)}</div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest mt-auto">
                            {k === 'video' && (pr
                              ? <a href={`/revue/${pr.id}`} className="text-orange hover:underline">Revue portail{counts[pr.id] ? ` (${counts[pr.id]})` : ''}</a>
                              : <button onClick={() => portalReview(f.name)} className="text-acier hover:text-orange" title="Mettre cette vidéo en revue dans le portail client (avec compte)">Revue portail</button>)}
                            <a href={url(f) + '&download=1'} className="text-acier hover:text-orange">Télécharger</a>
                            <button onClick={() => removeFile(f)} className="text-acier hover:text-red-300">Suppr.</button>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tous les liens de revue actifs */}
      {!client && links.length > 0 && (
        <section className="mt-8 border border-sky-300/30 rounded bg-sky-300/5 p-4">
          <h2 className="font-display text-xl tracking-wide">💬 Liens de revue actifs</h2>
          <div className="mt-3 space-y-2">
            {links.map(l => {
              const c = clientOfLink(l)
              return (
                <div key={l.token} className="border-t border-white/10 pt-2 text-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="flex-1 min-w-[180px] truncate">{c ? <button onClick={() => setClientId(c.id)} className="font-medium hover:text-orange">{clientLabel(c)}</button> : <span className="font-medium">{l.path}</span>}<span className="text-acier text-xs"> · {l.root === 'clients' ? 'livrables' : 'stock / ' + l.path} · créé {fmtDate(l.created_at)}</span></span>
                    <span className={`text-xs ${l.stats?.unresolved ? 'text-orange' : 'text-acier'}`}>{reviewStatsLabel(l)}</span>
                    <span className="flex items-center gap-3 text-xs uppercase tracking-widest whitespace-nowrap">
                      <button onClick={async () => { if (await copyText(l.url)) setMsg('🔗 Lien copié : ' + l.url) }} className="text-orange hover:underline">Copier</button>
                      <a href={l.url} target="_blank" rel="noreferrer" className="text-acier hover:text-orange">Ouvrir</a>
                      <button onClick={() => revokeLink(l)} className="text-acier hover:text-red-300">Révoquer</button>
                    </span>
                  </div>
                  {l.recent.filter(x => !x.resolved).slice(0, 3).map(x => (
                    <div key={x.id} className="mt-1 text-xs text-acier truncate border-l-2 border-white/10 pl-2"><span className="text-blanc">{x.author}</span>{x.time != null ? ` à ${fmtTc(x.time)}` : ''} sur {reviewTarget(x.file)} : {x.text}</div>
                  ))}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Revue portail (avec compte) */}
      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-2xl tracking-wide">Revue dans le portail client</h2>
            <p className="text-acier text-xs mt-1">Vidéos mises en revue pour les clients qui ont un compte : visibles dans « Mon espace », commentaires horodatés et approbation. {client ? `Filtré sur ${clientLabel(client)}.` : 'Tous les clients.'}</p>
          </div>
          <select className={inputCls + ' w-auto'} value={filter} onChange={e => setFilter(e.target.value)}><option value="tous">Tous les statuts</option>{REVIEW_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
        <div className="border border-white/10 rounded overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/5 text-acier text-[11px] uppercase tracking-widest"><tr><th className="text-left px-4 py-3">Vidéo</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Projet</th><th className="text-left px-4 py-3">Ajoutée</th><th className="text-center px-4 py-3">Commentaires</th><th className="text-left px-4 py-3">Statut</th><th className="px-4 py-3" /></tr></thead>
            <tbody>
              {filteredVideos.map(v => (
                <tr key={v.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3"><a href={`/revue/${v.id}`} className="font-medium hover:text-orange">🎬 {v.title}</a><div className="text-xs text-acier">{v.file_name}</div></td>
                  <td className="px-4 py-3"><button onClick={() => v.client_id && setClientId(v.client_id)} className="text-acier hover:text-orange">{cname(v.client_id)}</button></td>
                  <td className="px-4 py-3 text-acier">{pname(v.project_id)}</td>
                  <td className="px-4 py-3 text-acier">{fmtDate(v.created_at)}</td>
                  <td className="px-4 py-3 text-center">{counts[v.id] ? <span className="text-orange">{counts[v.id]} ouvert(s)</span> : <span className="text-acier">0</span>}</td>
                  <td className="px-4 py-3"><select className={inputCls + ' w-auto py-1'} value={v.review_status || 'en_attente'} onChange={e => setStatus(v, e.target.value)}>{REVIEW_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-xs uppercase tracking-widest"><a href={`/revue/${v.id}`} className="text-orange hover:underline mr-3">Ouvrir</a><button onClick={() => removeVideo(v)} className="text-acier hover:text-red-300">Retirer</button></td>
                </tr>
              ))}
              {filteredVideos.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-acier">Aucune vidéo en revue portail{client ? ' pour ce client. Utilisez « Revue portail » sur une vidéo ci-dessus.' : '.'}</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-acier mt-3">Statuts : {REVIEW_STATUSES.map(([k]) => <StatusBadge key={k} status={k} />).reduce((a: any[], b, i) => [...a, i ? ' ' : '', b], [])}</p>
      </section>
      {!client && <p className="text-xs text-acier mt-4">Sélectionnez un client pour gérer ses livrables (vidéos, photos), créer son lien de revue ou mettre une vidéo en revue portail.</p>}
    </Layout>
  )
}
